import userRest from '@/rest/user'
import { ElMessage } from 'element-plus'
import i18n from '@/lang'
import { getFormattedPhones } from '@/utils/formatPhone'

export default {
  state: () => ({
    userIsAuthorized: null,
    userIsInitialized: false,
    userData: null,
    readingFlightHistory: {}
  }),
  getters: {
    isAuthorized: (state) => !!state.userIsAuthorized,
    isUserInitialized: (state) => state.userIsInitialized,
    userName: (state) => state.userData?.name ? JSON.parse(state.userData.name) : null,
    userEmail: (state) => {
      if (state.userData?.attributes?.email) {
        return state.userData.attributes.email
      } else {
        return []
      }
    },
    userPhone (state) {
      if (state.userData?.attributes?.phone) {
        const phoneNumbers = state.userData.attributes.phone
        if (state.userData?.settings?.formatPhones) {
          return getFormattedPhones(phoneNumbers)
        }
        return phoneNumbers
      } else {
        return []
      }
    },
    userLocales: (state) => state.userData?.locales || ['ru', 'en'],
    userSettings: (state) => state.userData.settings,
    defaultSettings: (state, rootGetters) => {
      return {
        compactTable: false,
        displayIataMode: true,
        formatPhones: true,
        isDarkTheme: false,
        isDeskVisible: true,
        timeDeltaBegin: rootGetters.systemSettings.DEFAULT_UI_TIME_DELTA_BEGIN,
        timeDeltaEnd: rootGetters.systemSettings.DEFAULT_UI_TIME_DELTA_END
      }
    },
    historyByTimeFrame: (state) => (timeFrame) => {
      const userId = String(state.userData.id)
      if (state.readingFlightHistory?.[userId] && state.readingFlightHistory?.[userId][timeFrame]) {
        return state.readingFlightHistory[userId][timeFrame]
      } else {
        return null
      }
    },
    permissions: (state) => {
      return state.userData?.permissions || []
    },
    compactMode: (state) => {
      return state.userData?.settings?.compactTable || false
    },
    formatPhones: (state) => {
      return state.userData?.settings?.formatPhones
    },
    iataMode: (state) => {
      return state.userData?.settings?.displayIataMode
    },
    filterSettings: (state) => (filterName) => {
      if (filterName) {
        return state.userData?.settings[filterName] || []
      }

      return {
        desks: state.userData?.settings.desks || [],
        owners: state.userData?.settings.flightCodes || [],
        vehicleTypes: state.userData?.settings.vehicleTypes || []
      }
    }
  },
  mutations: {
    setUserData (state, payload) {
      if (payload) {
        state.userIsAuthorized = true
        state.userData = payload
      } else {
        state.userIsAuthorized = false
        state.userData = null
        state.userIsInitialized = false
      }
    },
    setUserSettings (state, payload) {
      state.userData.settings = payload
      state.userIsInitialized = true
    },
    addItemsToReadingHistory (state, { timeFrame, listOfId }) {
      const userId = String(state.userData.id)
      const userHistory = state.readingFlightHistory?.[userId]
      if (userHistory && timeFrame in userHistory) {
        const uniqueItems = new Set([...userHistory[timeFrame], ...listOfId])
        state.readingFlightHistory[userId][timeFrame] = [...uniqueItems]
      } else {
        state.readingFlightHistory[userId] = {
          ...state.readingFlightHistory[userId],
          ...{ [timeFrame]: [...listOfId] }
        }
      }
    },
    updateFilterSettings (state, payload) {
      const [filterName, codes] = payload

      state.userData.settings[filterName] = [...codes]
    }
  },
  actions: {
    login ({ commit, getters, dispatch }, { username, password }) {
      const formData = { username, password }

      return userRest.login(formData).then(async user => {
        await dispatch('initUser', user)
      }).catch((error) => {
        ElMessage({
          message: error,
          type: 'error',
          duration: 5 * 1000
        })
      })
    },
    shortLogin ({ dispatch }) {
      return userRest.shortLogin().then(async user => {
        await dispatch('initUser', user)
      }).catch((error) => {
        ElMessage({
          message: error,
          type: 'error',
          duration: 5 * 1000
        })
      })
    },
    async initUser({ commit, getters, dispatch }, user) {
      if (user) {
        // eslint-disable-next-line
        import.meta.env.DEV && console.log('User data:', user)

        if (!user.permissions.includes('read-briefings')) {
          throw new Error(i18n.global.t('errors.readBriefingsDenied'))
        }

        commit('setUserData', user)

        const settings = {}

        for (const key in user.settings) {
          let value = user.settings[key]

          switch (key) {
            case 'timeDeltaBegin':
            case 'timeDeltaEnd':
              // convert to numbers
              value = Number(value)
              break
            case 'compactTable':
            case 'displayIataMode':
            case 'formatPhones':
            case 'isDarkTheme':
            case 'isDeskVisible':
              // convert to boolean
              value = value === 'true'
              break
            case 'flightCodes':
            case 'vehicleTypes':
            case 'desks':
              value = JSON.parse(value)
          }

          settings[key] = value
        }

        if (settings.isDarkTheme) {
          commit('setColorScheme', 'dark')
        }

        await dispatch('initialize')
        commit('setUserSettings', Object.assign({}, getters.defaultSettings, settings))
      }
    },
    logout ({ commit }) {
      return userRest.logout().catch((error) => {
        ElMessage({
          message: error,
          type: 'error',
          duration: 5 * 1000
        })
      }).finally(() => {
        commit('setUserData', null)
        commit('clearUserNames')
      })
    }
  }
}
